import {ClipboardEvent, MutableRefObject, useEffect, useState} from 'react';

import {DEFAULT_ALLOWED_TAGS} from '../constants/defaultAllowedTags';
import {RichTextProps} from '../types';
import {insertHTML} from '../utils/editorCommands';
import {ensureHTML} from '../utils/ensureHTML';
import {normalizeValue} from '../utils/normalizeValue';
import {plainTextLength} from '../utils/plainTextLength';
import {sanitizeHTML} from '../utils/sanitizeHTML';
import {
  getCurrentSelectionRange,
  moveCaretToEnd,
  replaceEditorHTML,
} from '../utils/selectionHelpers';

export function useEditorValue(
  props: RichTextProps,
  editorRef: MutableRefObject<HTMLDivElement | null>,
  selectionRef: MutableRefObject<Range | null>,
  updateMetadata: () => void
) {
  const [currentValue, setCurrentValue] = useState(() =>
    normalizeValue(props.value, props.allowedTags)
  );

  const currentLength = plainTextLength(currentValue);
  const isOverLimit = !!props.maxLength && currentLength > props.maxLength;
  const warningThreshold = props.warningThreshold ?? 0.8;
  const isNearLimit =
    !!props.maxLength && !isOverLimit && currentLength >= props.maxLength * warningThreshold;
  const isEmpty = 0 === currentLength;

  useEffect(() => {
    const node = editorRef.current;

    if (!node) {
      return;
    }

    const nextValue = normalizeValue(props.value, props.allowedTags);

    if (node.innerHTML !== nextValue) {
      replaceEditorHTML(node, nextValue);
    }

    setCurrentValue(nextValue);
    updateMetadata();
  }, [props.value, props.allowedTags]);

  const syncEditorState = () => {
    const node = editorRef.current;

    if (!node) {
      return;
    }

    const sanitizedValue = sanitizeHTML(
      node.innerHTML,
      props.allowedTags ?? [...DEFAULT_ALLOWED_TAGS]
    );

    if (node.innerHTML !== sanitizedValue) {
      replaceEditorHTML(node, sanitizedValue);
      moveCaretToEnd(node);
      selectionRef.current = getCurrentSelectionRange(node);
    }

    const nextValue = plainTextLength(sanitizedValue) > 0 ? sanitizedValue : '';

    setCurrentValue(nextValue);
    props.onChange?.(nextValue);
  };

  const handleInput = () => {
    syncEditorState();
    updateMetadata();
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (props.isDisabled) {
      return;
    }

    const htmlContent = event.clipboardData.getData('text/html');
    const textContent = event.clipboardData.getData('text/plain');
    const nextContent = htmlContent
      ? sanitizeHTML(htmlContent, props.allowedTags ?? [...DEFAULT_ALLOWED_TAGS])
      : ensureHTML(textContent);

    insertHTML(editorRef.current, selectionRef, nextContent);
    syncEditorState();
    updateMetadata();
    props.onPaste?.(event);
  };

  return {
    currentValue,
    currentLength,
    isOverLimit,
    isNearLimit,
    isEmpty,
    handleInput,
    handlePaste,
    syncEditorState,
  };
}
